import { message } from 'antd';
import { axiosInstance } from '../Api';
import {
  IAdvancedAnalyticsApiPayload,
  IPreprocessedAnalyticsPayload,
  IScatterTableApiPayload,
} from '../../types/chart';
import { wrapCoordinates } from '../../components/features/Map/MapHelper';
import { IResult } from '../../types/api';
import { decrypt } from '../../utils/cryptoUtils';

export default class ChartRequest {
  private endPoint = 'Chart';

  // This API returns only the time series/scatter plot result.
  // Meant to be called for first best wave dataset with 1 year time range only
  // (last year of complete time range returned from previous GetMetadata call).
  async createBasicChartPreview(
    datasetName: string,
    startDate: string,
    endDate: string,
    coordinates: number[],
    isFwe?: boolean,
    pointName?: string,
    timeStepInterval?: number
  ) {
    const [longitude, latitude] = wrapCoordinates(coordinates);
    try {
      // always call without userId & key, in order to be consistent when user logs in after this call
      const r = await axiosInstance.post(
        `/${this.endPoint}/CreateBasicChartPreview`,
        {
          datasetName: `${datasetName}`,
          fileFormat: 'csv',
          location: `${longitude}, ${latitude}`,
          startDate: `${startDate}`,
          endDate: `${endDate}`,
          verticalLevel: null,
          isFwe: isFwe,
          pointName: pointName,
          TimestepInterval: timeStepInterval ?? 60,
        }
      );
      return r.data;
    } catch (error) {
      return { Result: null, Error: error };
    }
  }

  // This API returns only the rose plot result.
  // Meant to be called for first best wave dataset with full time range
  // (time range is returned from previous GetMetadata call).
  async createRosePlotPreview(
    datasetName: string,
    startDate: string,
    endDate: string,
    coordinates: number[],
    isFwe?: boolean,
    pointName?: string,
    timeStepInterval?: number
  ) {
    const [longitude, latitude] = wrapCoordinates(coordinates);
    // always call without userId & key, in order to be consistent when user logs in after this call
    const r = await axiosInstance.post(
      `/${this.endPoint}/CreateRosePlotPreview`,
      {
        datasetName: `${datasetName}`,
        fileFormat: 'csv',
        location: `${longitude}, ${latitude}`,
        startDate: `${startDate}`,
        endDate: `${endDate}`,
        verticalLevel: null,
        isFwe: isFwe,
        pointName: pointName,
        timeStepInterval: timeStepInterval ?? 60,
      }
    );
    return r.data.Result;
  }

  async getScatterTable(
    payload: IScatterTableApiPayload,
    options?: { signal?: AbortSignal }
  ) {
    const response = await axiosInstance.post(
      `/${this.endPoint}/GetScatterTable`,
      payload,
      {
        signal: options?.signal,
      }
    );

    return response.data;
  }

  async getAdvancedAnalytics(
    payload: IAdvancedAnalyticsApiPayload,
    options?: { signal?: AbortSignal }
  ) {
    const response = await axiosInstance.post(
      `/${this.endPoint}/GetAdvancedAnalytics`,
      payload,
      {
        signal: options?.signal,
      }
    );

    return response.data;
  }

  async getPreprocessedAnalytics(payload: IPreprocessedAnalyticsPayload) {
    try {
      const response = await axiosInstance.post(
        `/${this.endPoint}/PreProcessedAnalytics`,
        payload
      );
      return response.data;
    } catch (error) {
      return { Result: null, Error: error };
    }
  }

  // This API returns updated time series result.
  // You can call each of them after calling “CreateFullExtractionTasks” and get the orderId + requestId.
  // You can either wait for the full extraction status is completed for that requestId,
  // or even before that since I added a checking of extraction status and a time out = 2 mins, for each API.
  async getBasicChartV2(
    orderId: string,
    pointName: string,
    datasetId: string,
    startDate: string,
    endDate: string,
    location: string,
    timeStepInterval: number,
    parameters?: string,
    month?: number,
    options?: { signal?: AbortSignal }
  ) {
    let url = `/ChartNew/GetTimeseries?orderId=${orderId}&pointName=${pointName}&datasetId=${datasetId}&location=${location}&timestepInterval=${timeStepInterval}`;

    if (parameters) {
      url += `&parameterName=${encodeURIComponent(parameters)}`;
    }

    if (month) {
      url += `&month=${encodeURIComponent(month)}`;
    }

    url += `&startDate=${encodeURIComponent(
      startDate
    )}&endDate=${encodeURIComponent(endDate)}`;

    const encryptedResponse = await axiosInstance.get(url, {
      signal: options?.signal,
    });

    const r = decrypt(
      encryptedResponse.data.objectData,
      encryptedResponse.data.iv
    );
    return r;
  }

  async getJointScatterPlotImage(payload: IAdvancedAnalyticsApiPayload) {
    try {
      const response = await axiosInstance.post(
        `/${this.endPoint}/GetJointScatterPlot/img`,
        { ...payload }
      );
      return response.data;
    } catch (error) {
      return { Result: null, Error: error };
    }
  }

  // This API returns updated cross-dataset scatter plot result.
  // The API requires another set of items for dataset 2 to compare.
  async getJointScatterPlotV2(
    orderId: string,
    datasetName: string,
    parameters: string,
    orderId2: string,
    datasetName2: string,
    parameters2: string,
    startDate: string,
    endDate: string,
    pointName: string,
    location: string,
    timestepInterval: number,
    month?: number,
    options?: { signal?: AbortSignal }
  ) {
    let url =
      `/ChartNew/GetScatterPlot?orderId=${orderId}&location=${location}&pointName=${pointName}&datasetId=${datasetName}&timestepInterval=${timestepInterval}&parameterName=` +
      encodeURIComponent(parameters) +
      `&orderId2=${orderId2}&datasetId2=${datasetName2}&parameterName2=` +
      encodeURIComponent(parameters2) +
      `&startDate=` +
      encodeURIComponent(startDate) +
      `&endDate=` +
      encodeURIComponent(endDate);

    if (month) {
      url += `&month=${encodeURIComponent(month)}`;
    }

    const encryptedResponse = await axiosInstance.get(url, {
      signal: options?.signal,
    });
    const r = decrypt(
      encryptedResponse.data.objectData,
      encryptedResponse.data.iv
    );
    return r;
  }

  // This API returns updated time series result.
  // You can call each of them after calling “CreateFullExtractionTasks” and get the orderId + requestId.
  // You can either wait for the full extraction status is completed for that requestId,
  // or even before that since I added a checking of extraction status and a time out = 2 mins, for each API.
  async getRosePlotV2(
    orderId: string,
    pointName: string,
    datasetName: string,
    startDate: string,
    endDate: string,
    location: string,
    timeStepInterval: number,
    directionalDatasetId: string,
    month: number,
    legendInterval: number,
    valueColumnName?: string,
    directionColumnName?: string,
    degreeInterval?: number,
    options?: { signal?: AbortSignal }
  ) {
    const url =
      `/ChartNew/GetRosePlot?orderId=${orderId}&directionDatasetId=${directionalDatasetId}&month=${month}&pointName=${pointName}&datasetId=${datasetName}&timestepInterval=${timeStepInterval}&startDate=` +
      encodeURIComponent(startDate) +
      `&endDate=` +
      encodeURIComponent(endDate);
    const urlDegreeInterval = degreeInterval
      ? url + `&degreeInterval=${degreeInterval}`
      : url;
    const urlValueColumnName = valueColumnName
      ? urlDegreeInterval + `&columnName=` + encodeURIComponent(valueColumnName)
      : urlDegreeInterval;
    const urlDirectionColumnName = directionColumnName
      ? urlValueColumnName +
        `&directionalName=` +
        encodeURIComponent(directionColumnName)
      : urlValueColumnName;
    const completeUrl =
      urlDirectionColumnName + `&legendInterval=${legendInterval}`;

    const fixedUrl = completeUrl + `&location=${location}`;
    const encryptedResponse = await axiosInstance.get(fixedUrl, {
      signal: options?.signal,
    });
    const r = decrypt(
      encryptedResponse.data.objectData,
      encryptedResponse.data.iv
    );
    return r;
  }

  async getTimeseriesInsitu(
    idName: string,
    orderId: string,
    parameterName: string | number,
    datasetType: string,
    startDate: string,
    endDate: string
  ) {
    try {
      const url =
        `/InSitu/GetTimeSeries?orderId=${orderId}&idName=${idName}&parameterName=` +
        encodeURIComponent(parameterName) +
        `&type=` +
        encodeURIComponent(datasetType) +
        `&startDate=` +
        encodeURIComponent(startDate) +
        `&endDate=` +
        encodeURIComponent(endDate);
      const r = await axiosInstance.get(url);
      return r.data;
    } catch (error) {
      message.error(JSON.stringify(error));
      console.log(error);
    }
  }

  async getScatterPlotInsitu(
    idName: string,
    orderId: string,
    parameterName: string,
    startDate: string,
    endDate: string,
    type: string,
    datasetName: string
  ) {
    try {
      const url =
        `/InSitu/getScatterPlot?orderId=${orderId}&idName=${idName}&parameterName=` +
        encodeURIComponent(parameterName) +
        `&type=` +
        encodeURIComponent(type) +
        `&datasetName=` +
        encodeURIComponent(datasetName) +
        `&startDate=` +
        encodeURIComponent(startDate) +
        `&endDate=` +
        encodeURIComponent(endDate);
      const r = await axiosInstance.get(url);
      return r.data;
    } catch (error) {
      message.error(JSON.stringify(error));
      console.log(error);
    }
  }

  async getRosePlotInsitu(
    idName: string,
    orderId: string,
    numberOfDirection: number,
    parameterName: string,
    startDate: string,
    endDate: string
  ) {
    try {
      const url =
        `/InSitu/getRosePlot?orderId=${orderId}&idName=${idName}&numberOfDirection=${numberOfDirection}&parameterName=` +
        encodeURIComponent(parameterName) +
        `&start=` +
        encodeURIComponent(startDate) +
        `&end=` +
        encodeURIComponent(endDate);
      const r = await axiosInstance.get(url);
      return r.data;
    } catch (error: any) {
      message.error(JSON.stringify(error.message));
      console.log(error);
    }
  }
}
